import { getSWRPortfolioById } from '../../lib/simulation/swrPortfolioCalibration';
import { SWR_WITHDRAWAL_STRATEGY_VERSION } from '../../lib/simulation/swrWithdrawalStrategies';
import { resolveSWRCashMode } from '../../lib/simulation/swrCashModel';
import { isSWRFlowTaxPortfolio } from '../../lib/simulation/swrTaxModel';

// Stable saved identifiers are independent of the labels shown in the interface.
// This module only selects which controls must be available; it never normalises
// or changes the financial parameters that have been saved.
export const SWR_CONFIGURATION_LEVEL_STANDARD = 'standard';
export const SWR_CONFIGURATION_LEVEL_INTERMEDIATE = 'intermediate';
export const SWR_CONFIGURATION_LEVEL_ADVANCED = 'advanced';

export const SWR_CONFIGURATION_LEVELS = Object.freeze([
  Object.freeze({ id: SWR_CONFIGURATION_LEVEL_STANDARD, label: 'Base' }),
  Object.freeze({ id: SWR_CONFIGURATION_LEVEL_INTERMEDIATE, label: 'Intermedio' }),
  Object.freeze({ id: SWR_CONFIGURATION_LEVEL_ADVANCED, label: 'Avanzato' }),
]);

const CAPABILITIES = Object.freeze({
  [SWR_CONFIGURATION_LEVEL_STANDARD]: Object.freeze({ supportsIntermediate: false, supportsTechnical: false, supportsCapitalContributions: false }),
  [SWR_CONFIGURATION_LEVEL_INTERMEDIATE]: Object.freeze({ supportsIntermediate: true, supportsTechnical: false, supportsCapitalContributions: false }),
  [SWR_CONFIGURATION_LEVEL_ADVANCED]: Object.freeze({ supportsIntermediate: true, supportsTechnical: true, supportsCapitalContributions: true }),
});

export function getSWRConfigurationCapabilities(level) {
  return CAPABILITIES[level] || CAPABILITIES[SWR_CONFIGURATION_LEVEL_STANDARD];
}

const present = value => value !== null && value !== undefined && value !== '';
const differs = (value, expected) => present(value)
  && (typeof value !== 'number' || !Number.isFinite(value) || Math.abs(value - expected) > 1e-10);
const explicitSeed = value => present(value) && (typeof value !== 'string' || value.trim() !== '');

export function resolveSWRConfigurationLevel(params = {}) {
  if (!params || typeof params !== 'object') return SWR_CONFIGURATION_LEVEL_STANDARD;
  const requested = params.swrConfigurationMode;
  if (requested === SWR_CONFIGURATION_LEVEL_ADVANCED) return SWR_CONFIGURATION_LEVEL_ADVANCED;

  const historical = params.historicalPortfolio?.enabled === true;
  const preset = getSWRPortfolioById(params.portfolioPreset || params.modelPortfolio?.id);
  const manual = !historical && !preset;
  const strategy = params.withdrawalStrategy?.id || 'personalized';
  const activeBuffer = (strategy === 'personalized' || params.withdrawalStrategy?.version === SWR_WITHDRAWAL_STRATEGY_VERSION) && params.dynamicCashBufferEnabled === true;
  const expectedTaxRate = preset ? preset.taxRatePct / 100 : 0.26;
  const hasTechnicalAssumptions = (
    differs(params.annualPortfolioCostRate, 0)
    || differs(params.annualWealthTaxRate, 0.002)
    || differs(params.minimumIncomeCoverage, 1)
    // Contributions require their Advanced controls even when a legacy scenario
    // declared a simpler level. The stored events themselves are never changed.
    || (Array.isArray(params.capitalContributions) && params.capitalContributions.length > 0)
    || differs(params.numberOfSimulations, 5000)
    // The automatically recorded effective seed is provenance, not a user
    // request for the seed control. An explicit zero is still a real choice.
    || explicitSeed(params.randomSeed)
    || (present(params.maxSingleYearDrawdown))
    || (!historical && present(params.macroMode) && params.macroMode !== 'fixed')
    || (!isSWRFlowTaxPortfolio(params) && differs(params.taxRate, expectedTaxRate))
    || (manual && (params.customReturnParameterUncertaintyEnabled === true
      || params.returnParameterUncertaintyEnabled === true
      || params.returnParameterUncertainty?.enabled === true
      || params.returnParameterUncertaintyMode === 'custom-opt-in'))
    || (activeBuffer && resolveSWRCashMode(params) !== 'inflation-net')
  );
  if (hasTechnicalAssumptions) return SWR_CONFIGURATION_LEVEL_ADVANCED;

  const hasIntermediateAssumptions = (
    historical
    || strategy !== 'personalized'
    || params.planningMode === 'future'
    || params.futurePlanningEnabled === true
    || activeBuffer
    || params.withdrawalCustomization != null
  );
  return requested === SWR_CONFIGURATION_LEVEL_INTERMEDIATE || hasIntermediateAssumptions
    ? SWR_CONFIGURATION_LEVEL_INTERMEDIATE
    : SWR_CONFIGURATION_LEVEL_STANDARD;
}
